"use client";

import { useState, useEffect } from "react";

type Tab = {
  id: string;
  label: string;
  language?: string;
  code: string;
};

export default function CodeBlock({
  tabs,
  showHeader = true,
  filename,
  autoPlay = false,
  autoPlayInterval = 6000,
  className = "",
}: {
  tabs: Tab[];
  showHeader?: boolean;
  filename?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  className?: string;
}) {
  const [active, setActive] = useState(tabs[0]?.id);
  const [copied, setCopied] = useState(false);
  const [isInteracted, setIsInteracted] = useState(false);
  const [displayedLength, setDisplayedLength] = useState(0);

  const current = tabs.find((t) => t.id === active) ?? tabs[0];

  // Auto-play carousel logic
  useEffect(() => {
    if (!autoPlay || tabs.length <= 1 || isInteracted) return;

    const interval = setInterval(() => {
      setActive((currentActive) => {
        const currentIndex = tabs.findIndex((t) => t.id === currentActive);
        const nextIndex = (currentIndex + 1) % tabs.length;
        return tabs[nextIndex].id;
      });
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, tabs, isInteracted]);

  // Typewriter effect logic
  useEffect(() => {
    setDisplayedLength(0);
    const codeLen = current.code.length;
    
    // Calculate typing speed to finish well before autoPlay switches
    const typingDuration = 3000; // 3 seconds to type
    const frameRate = 20; // ms per frame
    const totalFrames = typingDuration / frameRate;
    const charsPerFrame = Math.max(1, Math.ceil(codeLen / totalFrames));

    const interval = setInterval(() => {
      setDisplayedLength((prev) => {
        if (prev >= codeLen) {
          clearInterval(interval);
          return codeLen;
        }
        return prev + charsPerFrame;
      });
    }, frameRate);

    return () => clearInterval(interval);
  }, [active, current.code]);

  const displayedCode = current.code.slice(0, displayedLength);
  const isTyping = displayedLength < current.code.length;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(current.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const handleTabClick = (id: string) => {
    setActive(id);
    setIsInteracted(true);
  };

  return (
    <div className={`relative flex flex-col overflow-hidden rounded-2xl border border-[#333] bg-[#0a0a0a] shadow-2xl ${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between gap-3 border-b border-[#333] bg-[#141414] px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f56]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#ffbd2e]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#27c93f]" />
            </div>
            {filename && (
              <span className="ml-3 hidden font-mono text-[11px] text-[#858585] sm:inline">{filename}</span>
            )}
          </div>
          {tabs.length > 1 && (
            <div className="flex min-w-0 items-center gap-0.5 overflow-x-auto scrollbar-hide">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleTabClick(t.id)}
                  className={`cursor-pointer shrink-0 whitespace-nowrap rounded px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                    active === t.id
                      ? "bg-[#0a0a0a] text-[#cccccc] shadow-[0_-1px_0_#007fd4_inset]"
                      : "text-[#858585] hover:bg-[#2d2d2d] hover:text-[#cccccc]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={onCopy}
            className="cursor-pointer flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium text-[#858585] transition-colors hover:bg-[#333] hover:text-[#cccccc]"
          >
            {copied ? (
              <>
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="m5 10 3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <rect x="7" y="7" width="9" height="9" rx="1.5" />
                  <path d="M13 7V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h2" strokeLinecap="round" />
                </svg>
                Copy
              </>
            )}
          </button>
        </div>
      )}

      <div className="relative flex-1 overflow-x-auto">
        <pre className="px-5 py-5 font-mono text-[13px] leading-[1.7] text-[#d4d4d4]">
          <code dangerouslySetInnerHTML={{ __html: highlight(displayedCode) }} />
          <span 
            className={`inline-block w-2 h-[15px] align-middle bg-[#d4d4d4] ml-0.5 animate-pulse ${
              isTyping ? "opacity-100" : "opacity-70"
            }`} 
            style={{ animationDuration: isTyping ? '0.2s' : '1.2s' }}
          />
        </pre>
      </div>

      {/* Carousel Dot Indicators */}
      {tabs.length > 1 && (
        <div className="flex items-center justify-center gap-2 pb-4 pt-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => handleTabClick(t.id)}
              className="cursor-pointer group relative flex h-3 w-3 items-center justify-center"
              aria-label={`Go to ${t.label}`}
            >
              <span 
                className={`absolute h-1.5 w-1.5 rounded-full transition-all duration-300 ${
                  active === t.id 
                    ? "bg-[#007fd4] scale-125 shadow-[0_0_8px_rgba(0,127,212,0.8)]" 
                    : "bg-white/20 group-hover:bg-white/40"
                }`} 
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// VS Code Dark+ theme highlighting logic
function highlight(code: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  let out = escape(code);

  const purpleKeywords = ["return", "await", "if", "else", "throw", "try", "catch", "new", "this"];
  const blueKeywords = ["import", "from", "export", "default", "const", "let", "var", "function", "true", "false", "null", "undefined", "class", "extends", "typeof", "in", "of", "async"];

  // Function calls (yellow/gold)
  out = out.replace(
    /([a-zA-Z_$][\w_$]*)(?=\s*\()/g,
    function(match) {
      if ([...purpleKeywords, ...blueKeywords].includes(match)) return match;
      return `<span style="color:#dcdcaa">${match}</span>`;
    }
  );

  // Block comments
  out = out.replace(
    /(\/\*[\s\S]*?\*\/)/g,
    '<span style="color:#6a9955">$1</span>',
  );
  // Line comments
  out = out.replace(
    /((?:^|\s)\/\/[^\n]*)/g,
    '<span style="color:#6a9955">$1</span>',
  );
  // Strings (template, single, double)
  out = out.replace(
    /(`(?:[^`\\]|\\.)*`|'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")/g,
    '<span style="color:#ce9178">$1</span>',
  );
  // Numbers
  out = out.replace(
    /\b(\d+(?:\.\d+)?)\b/g,
    '<span style="color:#b5cea8">$1</span>',
  );
  // Purple Keywords
  out = out.replace(
    new RegExp(`\\b(${purpleKeywords.join("|")})\\b`, "g"),
    '<span style="color:#c586c0">$1</span>',
  );
  // Blue Keywords
  out = out.replace(
    new RegExp(`\\b(${blueKeywords.join("|")})\\b`, "g"),
    '<span style="color:#569cd6">$1</span>',
  );
  
  // HTML/JSX Capitalized tag names (Teal)
  out = out.replace(
    /(&lt;\/?)([A-Z][\w.-]*)/g,
    '$1<span style="color:#4ec9b0">$2</span>',
  );
  // HTML/JSX lower case tag names (Blue)
  out = out.replace(
    /(&lt;\/?)([a-z][\w.-]*)/g,
    '$1<span style="color:#569cd6">$2</span>',
  );

  // HTTP verbs (for REST snippets)
  out = out.replace(
    /\b(GET|POST|PUT|DELETE|PATCH)\b/g,
    '<span style="color:#569cd6;font-weight:600">$1</span>',
  );

  return out;
}
